// externals
import { Fragment, useMemo } from 'react';

// libraries
import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';
import { SelectInput } from '@interstate/components/SelectInput';
import { Pagination } from '@interstate/components/Pagination';
import type { IVehicle } from '@makemydeal/dr-platform-types';

// local components
import { SearchResultHeader, VehicleColumnListItemsContainer } from './ChangeVehicleSearchResult.style';

// types
import { sortByOptions, aiSortByOptions, NOT_IN_STOCK_ALERT_MESSAGE } from '../../constants';
import type { CatalogVehicle, SortOption } from '../../types';
import { useSelector } from 'react-redux';
import { changeVehicleSelectors } from '../../store';
import { VehicleListRowItemInterstate } from '../vehicleListItem/VehicleListRowItem.interstate';
import { useVehicleSearchFiltersContext } from './VehicleSearchFiltersContext';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

export type ChangeVehicleSearchResultProps = {
    searchResults: CatalogVehicle[];
    isSearchError: boolean;
    showSortOptions: boolean;
    redirectToBuildVehicleTab: (vehicle: IVehicle) => void;
    aiHandlePageChange?: (selectedPage: number) => void;
};

const ChangeVehicleSearchResult: React.FC<ChangeVehicleSearchResultProps> = ({
    searchResults,
    isSearchError,
    redirectToBuildVehicleTab,
    showSortOptions,
    aiHandlePageChange
}) => {
    const hasSearchResults = searchResults.length > 0;

    const searchCurrentPage = useSelector(changeVehicleSelectors.getSearchCurrentPage);
    const searchPageCount = useSelector(changeVehicleSelectors.getSearchPageCount);
    const searchTotalCount = useSelector(changeVehicleSelectors.getSearchTotalCount);

    const aiSearchCurrentPage = useSelector(changeVehicleSelectors.getAgentSearchCurrentPage);
    const aiSearchPageCount = useSelector(changeVehicleSelectors.getAgentSearchPageCount);
    const aiSearchTotalCount = useSelector(changeVehicleSelectors.getAgentSearchTotalCount);

    const isDealerAISearchEnabled = useSelector(dealerSelectors.isDealerAISearchEnabled);
    const isAgeOfInventorySearchEnabled = useSelector(changeVehicleSelectors.isAgeOfInventorySearchEnabled);
    const currentPage = isDealerAISearchEnabled ? aiSearchCurrentPage : searchCurrentPage;
    const pageCount = isDealerAISearchEnabled ? aiSearchPageCount : searchPageCount;
    const totalCount = isDealerAISearchEnabled ? aiSearchTotalCount : searchTotalCount;
    const shouldShowAgeOfInventorySort = isDealerAISearchEnabled && isAgeOfInventorySearchEnabled;
    const currentSortByOptions = useMemo(
        () =>
            (!isDealerAISearchEnabled ? sortByOptions : aiSortByOptions).filter((option) =>
                shouldShowAgeOfInventorySort
                    ? option.value !== 'inventoryTypeAsc' && option.value !== 'inventoryTypeDesc'
                    : option.value !== 'ageAsc' && option.value !== 'ageDesc'
            ),
        [isDealerAISearchEnabled, shouldShowAgeOfInventorySort]
    );

    const alertMessage = useMemo(() => {
        if (searchResults.some((r) => !r.stockNumber)) return NOT_IN_STOCK_ALERT_MESSAGE;
        return undefined;
    }, [searchResults]);

    const getResultMessage = () => {
        const displayCount = totalCount;
        const formattedCount = displayCount.toLocaleString();

        return displayCount > 1 ? `Search Results (${formattedCount})` : 'Search Result';
    };

    const { sortBy, setSortBy, setCurrentPage, pageSize } = useVehicleSearchFiltersContext();

    const shouldShowPagination = totalCount > pageSize;

    const handlePageChange = (selectedPage: number) => {
        if (isDealerAISearchEnabled && aiHandlePageChange) {
            aiHandlePageChange(selectedPage);
        } else {
            setCurrentPage(selectedPage);
        }
    };

    return (
        <Fragment>
            {alertMessage && (
                <Alert data-testid="search-results-alert" type="info" role="" displayCloseButton sx={{ margin: '16px 0 8px 0' }}>
                    <Typography variant="body-sm" tag="span">
                        {alertMessage}
                    </Typography>
                </Alert>
            )}
            {hasSearchResults && !isSearchError && (
                <div data-testid="search-results-container" className="search-results-container">
                    <SearchResultHeader>
                        <Typography
                            variant="h3"
                            className="search-result-title"
                            data-testid="search-result-title"
                            sx={{ margin: '0' }}
                        >
                            {getResultMessage()}
                        </Typography>
                        {showSortOptions && (
                            <SelectInput
                                data-testid="search-results-sort-by"
                                label="Sort By"
                                options={currentSortByOptions}
                                value={sortBy}
                                virtualized
                                onChange={(e) => {
                                    setSortBy(e.target.value as SortOption);
                                }}
                                size="small"
                                displayDeselectOption={false}
                            />
                        )}
                    </SearchResultHeader>
                    <VehicleColumnListItemsContainer>
                        {searchResults.map((vehicle, cardIndex) => (
                            <VehicleListRowItemInterstate
                                vehicle={{ ...vehicle, cardIndex: cardIndex }}
                                key={`${vehicle.vin || vehicle.chromeStyleId || vehicle.stockNumber || cardIndex}-${cardIndex}`}
                                redirectToBuildVehicleTab={redirectToBuildVehicleTab}
                            ></VehicleListRowItemInterstate>
                        ))}
                    </VehicleColumnListItemsContainer>
                    {shouldShowPagination && (
                        <Pagination
                            data-testid="search-results-pagination"
                            count={pageCount}
                            page={currentPage}
                            onChange={handlePageChange}
                            showFirstButton
                            sx={{ margin: '16px 0', display: 'flex', justifyContent: 'center' }}
                        />
                    )}
                </div>
            )}
        </Fragment>
    );
};

export default ChangeVehicleSearchResult;
